/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import useTryCatch from "hooks/useTryCatch";
import { createCompanion, updateCompanion, updateUser } from "lib/userApi";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useUser } from "state/UserContext";

import useCompanion from "hooks/useCompanion";
import { usePrefNavigator } from "state/PrefNavigatorContext";

const useSubmitGuestForm = (
  formDataFromParent,
  formErrors,
  setFormErrors,
  isEditing,
  conditions,
  allFieldKeys,
  categoryId,
  validateDynamicFields,
  getDynamicFieldKeys,
  dynamicFieldErrors,
  customPrefixValue,
  flightOptionID,
  dynamicFields,
) => {
  const { navigateWithEvent, eventId } = useEventRouting();
  const { setLoading, userData } = useUser();
  const { userId } = useParams();
  const { hasEnabledStep } = usePrefNavigator();
  const validateForm = () => {
    let isValid = true;
    // const errors = {};

    // Validate dynamic fields if validation function is provided
    if (validateDynamicFields && typeof validateDynamicFields === "function") {
      const isDynamicFieldsValid = validateDynamicFields();
      if (!isDynamicFieldsValid) {
        isValid = false;
        toast.error(`Please fill all the required fields`);
      }
    }

    if (dynamicFieldErrors && typeof dynamicFieldErrors === "object") {
      const hasErrors = Object.values(dynamicFieldErrors).some(
        (msg) => typeof msg === "string" && msg.trim().length > 0,
      );
      if (hasErrors) {
        isValid = false;
        toast.error("Please resolve the highlighted errors before saving");
      }
    }

    return isValid;
  };
  const [companion, , updateExistingCompanionOrCreateNewCompanion] =
    useCompanion(userId);
  const {
    documents,
    flightDetails,
    flightNotes,
    // passportsubmittion,
    // passportValid,
    // validVisa,
    selectedPref,
    flightType,
  } = formDataFromParent;

  const handleOnSubmit = useTryCatch(
    async (e) => {
      e.preventDefault();
      if (!validateForm()) {
        return;
      }
      let isCompanion = companion ? companion.id !== userData?.id : true;

      const formData = new FormData();
      // Add dynamic field values to form data

      let body = {
        isCompanion: companion ? companion.id !== userData?.id : true,
        self_book_flight: conditions.willBookFlight,
      };

      formData.append("is_companion", isCompanion);
      formData.append("self_book_flight", conditions.willBookFlight);

      if (documents.passport.value instanceof File) {
        formData.append("passport_image", documents.passport.value);
        body.passport_image = documents.passport.value;
      }
      if (documents.portrait.value instanceof File) {
        formData.append("personal_image", documents.portrait.value);
        body.personal_image = documents.portrait.value;
      }
      if (!conditions.willBookFlight) {
        body = {
          ...body,
          ...flightDetails,
          flight_notes: flightNotes,
          outbound_flight_preferred_day_period:
            selectedPref?.outboundSelectedPrefTime,
          return_flight_preferred_day_period:
            selectedPref?.returnSelectedPrefTime,
          outbound_flight_preferred_date:
            selectedPref?.outboundSelectedPrefDate,
          return_flight_preferred_date: selectedPref?.returnSelectedPrefDate,
        };
        formData.append("flight_notes", flightNotes);

        formData.append(
          "outbound_flight_preferred_day_period",
          selectedPref?.outboundSelectedPrefTime || "",
        );
        formData.append(
          "return_flight_preferred_day_period",
          selectedPref?.returnSelectedPrefTime || "",
        );
        formData.append(
          "outbound_flight_preferred_date",
          selectedPref?.outboundSelectedPrefDate || "",
        );
        formData.append(
          "return_flight_preferred_date",
          selectedPref?.returnSelectedPrefDate || "",
        );
      }

      if (flightType === 2) {
        body.flight_type = 2;
        formData.append("flight_type", 2);
      }

      // Always append flight details fields. Guard against undefined and coerce undefined values to empty string.
      if (flightDetails && typeof flightDetails === "object") {
        Object.keys(flightDetails).forEach((key) => {
          const value = conditions.willBookFlight ? "" : flightDetails[key];
          formData.append(key, value == null ? "" : value);
        });
      }

      if (getDynamicFieldKeys) {
        const dynamicFieldValues = getDynamicFieldKeys(customPrefixValue);

        // Define fields to ignore when appending to form data
        const fieldsToIgnore = ["image", "passport_image"];

        Object.entries(dynamicFieldValues).forEach(([key, value]) => {
          // Skip fields that should be ignored
          if (fieldsToIgnore.includes(key)) {
            return;
          }

          // Skip relationship field if not a companion
          if (key === "relationship" && !isCompanion) {
            return;
          }

          if (Array.isArray(value)) {
            // For multi-select fields, join values with comma
            formData.append(key, value.join("||"));
          } else {
            formData.append(key, value ?? "");
          }
        });

        // Handle country codes for phone number fields with object keys
        // Dynamically check for country codes based on dynamic field definitions
        Object.entries(dynamicFields || {}).forEach(([fieldKey, field]) => {
          if (
            field.type === "phone_number" &&
            typeof field.key === "object" &&
            field.key.code &&
            field.key.number
          ) {
            // Map country code fields to their corresponding form data
            const countryCodeKey = field.key.code;
            let countryCodeValue = "";

            // Get the appropriate country code value based on the field key
            if (countryCodeKey === "assistant_country_code") {
              countryCodeValue = formDataFromParent.assistantCountryCode || "";
            } else if (
              countryCodeKey === "emergency_contact_country_code" ||
              countryCodeKey === "emergency_country_code"
            ) {
              countryCodeValue = formDataFromParent.emergencyCountryCode || "";
            } else if (countryCodeKey === "country_code") {
              countryCodeValue = formDataFromParent.countryCode || "";
            }

            // Always append the country code field, even if empty
            formData.append(countryCodeKey, countryCodeValue);
          }
        });
      }
      formData.append("registration_form_category_id", categoryId);
      setLoading(true);
      let userId;
      let fetcher;
      const isUser = companion?.hash_id ? true : false;
      if (isUser) {
        userId = userData?.hash_id;
        fetcher = async () => updateUser(formData, undefined, eventId);
      } else {
        if (companion?.hasOwnProperty("id")) {
          userId = companion.id;
          fetcher = async () =>
            updateCompanion(companion.id, formData, eventId);
        } else {
          body.user_id = userData?.hash_id;
          formData.append("user_id", userData?.hash_id);
          fetcher = async () => createCompanion(formData, eventId);
        }
      }
      const { data } = await fetcher();
      setLoading(false);
      if (!userId) {
        userId = data?.companion_id;
        body = {
          ...body,
          // to stop storing "[object file]" in the state
          personal_image: "",
          id: data?.companion_id,
        };
      }
      // @@ NEED REVIEW...Using body instead of server response...
      updateExistingCompanionOrCreateNewCompanion({
        ...body,
        id: userId,
      });
      if (isEditing && !hasEnabledStep) {
        return navigateWithEvent(ROUTES.dashboard.base);
      }
      return navigateWithEvent(ROUTES.choices.base(userId));
    },
    {
      setLoading,
      onError: (res) => {
        const { data } = res;
        if (data && data.hasOwnProperty("companion")) {
          updateExistingCompanionOrCreateNewCompanion(data.companion);
          return navigateWithEvent(
            ROUTES.onboard.personalInfo(data.companion.id),
            {
              replace: true,
            },
          );
        }
      },
    },
  );
  return {
    handleOnSubmit,
    validateForm,
  };
};

export default useSubmitGuestForm;
